import { computed, onMounted, ref, watch } from 'vue'

const STORAGE_KEY = 'church-client-theme'
/** @type {import('vue').Ref<'light'|'dark'|'system'>} */
const preference = ref('system')
/** @type {import('vue').Ref<'light'|'dark'>} */
const resolved = ref('light')

function systemPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function applyTheme(mode) {
  const dark = mode === 'dark'
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light')
  document.documentElement.classList.toggle('dark', dark)
  resolved.value = dark ? 'dark' : 'light'
}

function resolve(pref) {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light'
  return pref === 'dark' ? 'dark' : 'light'
}

export function useTheme() {
  function setTheme(pref) {
    preference.value = pref
    try {
      localStorage.setItem(STORAGE_KEY, pref)
    } catch {
      /* ignore */
    }
    applyTheme(resolve(pref))
  }

  function cycleTheme() {
    const order = ['light', 'dark', 'system']
    const i = order.indexOf(preference.value)
    setTheme(order[(i + 1) % order.length])
  }

  onMounted(() => {
    let saved = 'system'
    try {
      saved = localStorage.getItem(STORAGE_KEY) || 'system'
    } catch {
      /* ignore */
    }
    if (!['light', 'dark', 'system'].includes(saved)) saved = 'system'
    preference.value = saved
    applyTheme(resolve(saved))

    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (mq) {
      const onChange = () => {
        if (preference.value === 'system') applyTheme(resolve('system'))
      }
      mq.addEventListener?.('change', onChange)
    }
  })

  watch(preference, (p) => applyTheme(resolve(p)))

  return {
    preference,
    resolved,
    isDark: computed(() => resolved.value === 'dark'),
    setTheme,
    cycleTheme,
    options: [
      { label: 'Light', value: 'light', icon: 'lucide-sun' },
      { label: 'Dark', value: 'dark', icon: 'lucide-moon' },
      { label: 'System', value: 'system', icon: 'lucide-monitor' },
    ],
  }
}

/** Call once at app boot before paint if possible */
export function initThemeFromStorage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) || 'system'
    applyTheme(resolve(saved))
    preference.value = saved
  } catch {
    applyTheme('light')
  }
}
